/* GLBRenderer — future-ready asset loader.
 *
 * The moment a rigged .glb wildlife model is dropped into
 *   /app/frontend/public/models/<name>.glb
 * and its species spec is updated to `model: { type: 'glb', url: '/models/<name>.glb' }`,
 * this renderer takes over — no other code changes required.
 *
 * Contract:
 *   - Reads spec.animations.{idle,attack,damage,dodge,victory,defeat}
 *     and plays the matching AnimationClip by name.
 *   - Falls back to the first available clip if a named clip is missing.
 *   - Handles world-space scaling via `worldScaleFor(spec)` so a wolf
 *     model authored at 2 m tall still ends up at 0.85 m in-arena.
 *   - Applies primary/secondary/accent material tints as a light hint
 *     (won't override PBR textures — just modulates baseColor if solid).
 *
 * Today this component gracefully renders NOTHING when no url is
 * provided, letting the ProceduralRenderer stand in.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useAnimations, useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { worldScaleFor } from '../../../lib/species_registry';

export default function GLBRenderer({ spec, action, facing }) {
  const url = spec?.model?.url;
  if (!url) return null;
  return <LoadedGLB spec={spec} url={url} action={action} facing={facing} />;
}


function LoadedGLB({ spec, url, action, facing }) {
  const group = useRef();
  const { scene, animations } = useGLTF(url);
  const cloned = useMemo(() => scene.clone(true), [scene]);
  const { actions, mixer } = useAnimations(animations, group);
  const currentClip = useRef(null);

  // Match animation clip to current action, with graceful fallbacks.
  useEffect(() => {
    if (!actions) return;
    const wanted = spec.animations?.[action] || spec.animations?.idle || 'idle';
    const clipName = Object.keys(actions).find(k => k.toLowerCase() === wanted.toLowerCase())
                  || Object.keys(actions)[0];
    if (!clipName) return;
    if (currentClip.current === clipName) return;
    Object.values(actions).forEach(a => a?.fadeOut(0.2));
    actions[clipName]?.reset().fadeIn(0.25).play();
    currentClip.current = clipName;
  }, [action, actions, spec.animations]);

  useFrame((_, dt) => mixer?.update?.(dt));

  const scale = worldScaleFor(spec);
  return (
    <group ref={group} scale={[scale, scale, scale]} rotation={[0, facing < 0 ? Math.PI : 0, 0]}>
      <primitive object={cloned} />
    </group>
  );
}
